export interface SharePayload {
  heading: string;
  result: string;
  context?: string;
  link?: string;
}

const SITE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export function shareLink(p: SharePayload) {
  if (p.link) return p.link;
  const qs = new URLSearchParams({ h: p.heading.slice(0, 80), r: p.result.slice(0, 80), d: (p.context || "").slice(0, 90) });
  return `${SITE}/api/share/r?${qs.toString()}`;
}

export const groupShareLink = (groupId: string) => `${SITE}/api/share/g/${groupId}`;

const W = 1080;
const H = 1350;
const INK = "#121212";

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const raw of text.split(/\s+/)) {
    let word = raw;
    while (ctx.measureText(word).width > maxW) {
      let i = word.length;
      while (i > 1 && ctx.measureText(word.slice(0, i)).width > maxW) i--;
      if (line) { lines.push(line); line = ""; }
      lines.push(word.slice(0, i));
      word = word.slice(i);
    }
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = word;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

function strokeFill(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fill: string, stroke: number, shadow = 0) {
  ctx.lineJoin = "round";
  if (shadow) {
    ctx.fillStyle = INK;
    ctx.lineWidth = stroke;
    ctx.strokeStyle = INK;
    ctx.strokeText(text, x + shadow, y + shadow);
    ctx.fillText(text, x + shadow, y + shadow);
  }
  ctx.lineWidth = stroke;
  ctx.strokeStyle = INK;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}

function drawBackground(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = "#FFE600";
  ctx.fillRect(0, 0, W, H);
  ctx.save();
  ctx.translate(W / 2, H * 0.45);
  ctx.fillStyle = "#FFD000";
  for (let i = 0; i < 40; i += 2) {
    const a1 = (i / 40) * Math.PI * 2;
    const a2 = ((i + 1) / 40) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(a1) * 2000, Math.sin(a1) * 2000);
    ctx.lineTo(Math.cos(a2) * 2000, Math.sin(a2) * 2000);
    ctx.fill();
  }
  ctx.restore();
  ctx.lineWidth = 18;
  ctx.strokeStyle = INK;
  ctx.strokeRect(9, 9, W - 18, H - 18);
}

function drawResult(ctx: CanvasRenderingContext2D, result: string) {
  let size = 150;
  let lines: string[] = [];
  while (size >= 52) {
    ctx.font = `700 ${size}px Fredoka, sans-serif`;
    lines = wrap(ctx, result.toUpperCase(), 740);
    if (lines.length * size * 1.05 <= 440) break;
    size -= 8;
  }
  if (lines.length > 5) lines = [...lines.slice(0, 4), `${lines[4].slice(0, -1)}…`];
  const top = 700 - (lines.length * size * 1.05) / 2 + size * 0.85;
  lines.forEach((l, i) => strokeFill(ctx, l, W / 2, top + i * size * 1.05, "#FF5D1F", Math.max(6, size / 12), 8));
}

export async function renderShareCard(p: SharePayload): Promise<Blob> {
  try {
    await (document as any).fonts?.ready;
  } catch {
    /* fonts api missing */
  }
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  ctx.textAlign = "center";
  drawBackground(ctx);

  roundRect(ctx, 132, 322, 860, 760, 56);
  ctx.fillStyle = INK;
  ctx.fill();
  roundRect(ctx, 110, 300, 860, 760, 56);
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.lineWidth = 12;
  ctx.stroke();

  ctx.save();
  ctx.translate(W / 2, 300);
  ctx.rotate(-0.05);
  roundRect(ctx, -320, -78, 640, 150, 75);
  ctx.fillStyle = "#FF2A85";
  ctx.fill();
  ctx.lineWidth = 10;
  ctx.stroke();
  ctx.font = "700 76px Fredoka, sans-serif";
  strokeFill(ctx, "DITHER SAYS...", 0, 24, "#FFFFFF", 10);
  ctx.restore();

  ctx.font = "800 38px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = "#52525B";
  const heading = p.heading.toUpperCase();
  ctx.fillText(heading.length > 34 ? `${heading.slice(0, 33)}…` : heading, W / 2, 440);
  drawResult(ctx, p.result);
  if (p.context) {
    ctx.font = "700 36px 'Plus Jakarta Sans', sans-serif";
    ctx.fillStyle = INK;
    const c = p.context.length > 44 ? `${p.context.slice(0, 43)}…` : p.context;
    ctx.fillText(c, W / 2, 1000);
  }
  ctx.font = "700 140px Fredoka, sans-serif";
  strokeFill(ctx, "DITHER", W / 2, 1245, "#FF5D1F", 14, 10);
  ctx.font = "800 34px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = INK;
  ctx.fillText("decisions in seconds", W / 2, 1300);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b as Blob), "image/png"));
}

export function shareText(p: SharePayload) {
  return `DITHER says: ${p.result}! (${p.heading}) — your turn: ${shareLink(p)}`;
}

export async function shareCard(p: SharePayload, blob: Blob): Promise<"shared" | "downloaded"> {
  const file = new File([blob], "dither-result.png", { type: "image/png" });
  const nav = navigator as any;
  if (nav.canShare?.({ files: [file] })) {
    await nav.share({ files: [file], title: "DITHER says...", text: shareText(p), url: shareLink(p) });
    return "shared";
  }
  downloadBlob(blob);
  return "downloaded";
}

export function downloadBlob(blob: Blob, name = "dither-result.png") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
